import {
  createContext as rcCreateContext,
  useContext as rcUseContext,
} from 'react';

import type { Maybe } from '~/typings/utils';
import type { CreateContextOptions, CreateContextReturn } from './react.types';
import { isNil } from 'lodash-es';

const CONTEXT_ERROR_MESSAGE =
  'useContext must be inside a Provider with a value';

/**
 * Creates a named context, provider, and hook. An effective way to create context.
 *
 * https://juliangaramendy.dev/blog/strict-react-context
 * https://kentcdodds.com/blog/how-to-use-react-context-effectively
 *
 * @param options create context options
 */
const createContext = <ContextType>(options: CreateContextOptions = {}) => {
  const { strict = true, errorMessage = CONTEXT_ERROR_MESSAGE, name } = options;

  const Context = rcCreateContext<Maybe<ContextType>>(null);

  Context.displayName = name;

  const useContext = () => {
    const context = rcUseContext(Context);
    if (strict && isNil(context)) {
      throw new Error(errorMessage);
    }
    return context;
  };

  return [
    Context.Provider,
    useContext,
    Context,
  ] as CreateContextReturn<ContextType>;
};

export default createContext;
